import React from "react";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";
import {
    usePagination,
    useTable,
    useGlobalFilter,
    useRowSelect,
} from "react-table";
import BTable from "react-bootstrap/Table";
import swal from "@sweetalert/with-react";
import NProgress from "nprogress";
import { useSnapshot } from "valtio";
import {
    AngleSmallLeftIcon,
    StyledButton,
    StyledIconButton,
    StyledSearchInput,
    TrashIcon,
} from "@aureskonnect/react-ui";

import { store as vStore } from "@vStore";
import store, { setGroupedSubFamilies } from "./store";

import { ITEMS_PER_PAGE } from "@constants/index";

import { NoImagePlaceholderIcon } from "../components/NoImagePlaceholderIcon";
import Pagination from "@components/Common/StatisticsPagination";

export default function FourthStep({ go }: any) {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(vStore);
    const { groupedSubFamilies, groupName } = useSnapshot(store);

    const [selectedSubFamilies, setSelectedSubFamilies] = React.useState<any[]>(
        []
    );

    const handleRemoveSelectedSubFamily = React.useCallback(
        (subFamily: any) => {
            let localSelectedSubFamilies: any[] = [
                ...selectedSubFamilies,
                subFamily,
            ];
            setSelectedSubFamilies(localSelectedSubFamilies);
            let localSubFamilies: any[] = groupedSubFamilies.filter(
                (subFamily: any) => {
                    return !localSelectedSubFamilies
                        .map((selectedSubFamily: any) =>
                            `${selectedSubFamily.sub_family}|${selectedSubFamily.family}`.toLowerCase()
                        )
                        .includes(
                            `${subFamily.sub_family}|${subFamily.family}`.toLowerCase()
                        );
                }
            );

            setGroupedSubFamilies(localSubFamilies);
        },
        [selectedSubFamilies, groupedSubFamilies]
    );

    const columns = React.useMemo(
        () => [
            {
                Header: t("Subfamily"),
                accessor: "sub_family",
            },
            {
                Header: t("Picture"),
                accessor: function (subFamily: any) {
                    return (
                        <div>
                            {subFamily.image === "" ? (
                                <NoImagePlaceholderIcon />
                            ) : (
                                subFamily.image
                            )}
                        </div>
                    );
                },
                disableGlobalFilter: true,
            },
            {
                Header: t("Family"),
                accessor: "family",
                disableGlobalFilter: true,
            },
            {
                Header: "",
                id: "actions",
                disableGlobalFilter: true,
                accessor: function (subFamily: any) {
                    return (
                        <div>
                            <StyledButton
                                rounded={true}
                                outline={true}
                                variant={mainColor === null ? "secondary" : ""}
                                className="d-none d-sm-none d-md-block white-space-nowrap"
                                style={{
                                    color: mainColor,
                                    borderColor: mainColor,
                                    float: "right",
                                }}
                                onClick={() =>
                                    handleRemoveSelectedSubFamily(subFamily)
                                }
                            >
                                {t("Delete")}
                            </StyledButton>
                            <StyledIconButton
                                className="d-block d-sm-block d-md-none m-0"
                                icon="TrashIcon"
                                onClick={() =>
                                    handleRemoveSelectedSubFamily(subFamily)
                                }
                            >
                                <TrashIcon height={20} width={20} />
                            </StyledIconButton>
                        </div>
                    );
                },
            },
        ],
        [t, handleRemoveSelectedSubFamily, mainColor]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data: groupedSubFamilies,
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        usePagination,
        useRowSelect
    );

    function handlePreviousStepAction() {
        go(1);
    }

    async function handleSaveButtonClickEvent() {
        let apiUrl = `${process.env.REACT_APP_API_V1_URL}/settings/sub-families/groups`;
        let savedData = {
            designation: groupName,
            groupment: groupedSubFamilies.map(
                (s: any) => `${s.sub_family}|${s.family}`
            ),
        };

        try {
            NProgress.start();

            mutate(
                apiUrl,
                await fetch(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${localStorage.getItem("jwt")}`,
                    },
                    body: JSON.stringify(savedData),
                    method: "POST",
                })
                    .then((response) => response.json())
                    .then((data) => {
                        NProgress.done();

                        if (data.error) {
                            throw Error("Error while saving selection!");
                        }

                        go(0);
                        setGroupedSubFamilies([]);

                        return swal({
                            icon: "success",
                            content: <p>{t(data.message)}!</p>,
                            buttons: false,
                            timer: 2000,
                        });
                    })
            );
        } catch (e: any) {
            NProgress.done();

            return swal({
                icon: "error",
                content: <p>{t("There's an error")!}</p>,
                buttons: false,
                timer: 2000,
            });
        }
    }

    React.useEffect(() => {
        setPageSize(ITEMS_PER_PAGE);
    }, [setPageSize]);

    return (
        <React.Fragment>
            <div className="d-md-flex justify-content-md-between">
                <div className="d-md-flex align-items-md-center my-2">
                    <div className="d-flex align-items-center">
                        <StyledIconButton
                            icon="AngleSmallLeftIcon"
                            className="m-0 p-0"
                            onClick={handlePreviousStepAction}
                        >
                            <AngleSmallLeftIcon height={35} width={35} />
                        </StyledIconButton>

                        <span className="mb-0 ml-2 mr-0 font-size-14 h5 white-space-nowrap">
                            {groupName}
                        </span>
                    </div>
                    <span
                        className="divider mt-0 mx-4 d-none d-md-block"
                        style={{
                            backgroundColor: "#808080a6",
                            height: "2.25rem",
                            width: "2px",
                        }}
                    />
                    <StyledSearchInput
                        onChange={(e) => {
                            setGlobalFilter(e.target.value);
                        }}
                        placeholder={`${t("Search")} ...`}
                        className="p-0 mx-2 mx-md-0 mt-2 mt-md-0"
                    />
                </div>
                <StyledButton
                    rounded={true}
                    variant="success"
                    className="mr-0 ml-2 mt-0 my-md-2 w-xs-95 mx-md-2 w-sm-95 w-md-unset"
                    onClick={handleSaveButtonClickEvent}
                    disabled={pageCount === 0}
                >
                    {t("Save")}
                </StyledButton>
            </div>
            {page.length > 0 ? (
                <React.Fragment>
                    <BTable responsive {...getTableProps()}>
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            className="white-space-nowrap"
                                            {...column.getHeaderProps()}
                                        >
                                            {column.render("Header")}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.map((row, i) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td
                                                    className="white-space-nowrap align-middle"
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render("Cell")}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </BTable>
                    {groupedSubFamilies.length > ITEMS_PER_PAGE ? (
                        <Pagination
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            pageIndex={pageIndex}
                        />
                    ) : null}
                </React.Fragment>
            ) : null}
        </React.Fragment>
    );
}
